/*
 * Purpose: Category-grouped builder nav — activity cards + sub-items (not flat wide tabs)
 * Last modified: 2026-06-26 | Agent
 * Completeness: 90
 */
import { cn } from '@/lib/utils';
import {
  BUILDER_NAV_GROUPS,
  type BuilderPanelId,
  type ProductActivityId,
  panelActivity,
} from '@/lib/productActivities';
import { Card } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { PRODUCT_TERMS } from '@/lib/productTerms';

export interface BuilderActivityNavProps {
  activePanel: BuilderPanelId;
  onSelectPanel: (id: BuilderPanelId) => void;
  onOpenCompose?: () => void;
  className?: string;
}

export function BuilderActivityNav({
  activePanel,
  onSelectPanel,
  onOpenCompose,
  className,
}: BuilderActivityNavProps) {
  const activeActivity = panelActivity(activePanel);

  return (
    <nav
      className={cn('flex flex-col min-h-0 bg-card border-border', className)}
      aria-label="Builder activities"
    >
      {/* Mobile: primary activities as half-width card grid */}
      <div className="lg:hidden shrink-0 p-3 border-b border-border space-y-3">
        <p className="text-xs font-medium text-muted-foreground px-0.5">Activities</p>
        <div className="grid grid-cols-2 gap-2">
          {BUILDER_NAV_GROUPS.map((group) => {
            const isActive = group.activityId === activeActivity;
            const firstItem = group.items[0];
            return (
              <button
                key={group.activityId}
                type="button"
                onClick={() => onSelectPanel(firstItem.id)}
                className="text-left"
              >
                <Card
                  className={cn(
                    'p-3 h-full ui-lift-card-subtle transition-colors',
                    isActive && 'ring-2 ring-primary/40 border-primary/30'
                  )}
                >
                  <p className="text-sm font-semibold leading-tight">{group.label}</p>
                  <p className="text-[10px] text-muted-foreground mt-1 line-clamp-2">
                    {group.description}
                  </p>
                </Card>
              </button>
            );
          })}
        </div>
        {onOpenCompose && (
          <button type="button" onClick={onOpenCompose} className="w-full text-left">
            <Card className="p-3 ui-lift-card-subtle border-dashed">
              <p className="text-sm font-semibold">{PRODUCT_TERMS.autoBlockFlowComposer}</p>
              <p className="text-[10px] text-muted-foreground mt-1">
                Plan outcome → dry run (separate workspace)
              </p>
            </Card>
          </button>
        )}
      </div>

      {/* Desktop: category list with sub-items */}
      <ScrollArea className="flex-1 min-h-0">
        <div className="hidden lg:block p-3 space-y-4">
          {onOpenCompose && (
            <button
              type="button"
              onClick={onOpenCompose}
              className="w-full text-left rounded-lg border border-dashed border-border p-3 hover:bg-muted/40 transition-colors"
            >
              <p className="text-sm font-semibold">{PRODUCT_TERMS.autoBlockFlowComposer}</p>
              <p className="text-xs text-muted-foreground mt-0.5">Open compose workspace</p>
            </button>
          )}

          {BUILDER_NAV_GROUPS.map((group) => (
            <div key={group.activityId}>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground px-2 mb-1.5">
                {group.label}
              </p>
              <ul className="space-y-0.5">
                {group.items.map((item) => {
                  const isActive = activePanel === item.id;
                  return (
                    <li key={item.id}>
                      <button
                        type="button"
                        onClick={() => onSelectPanel(item.id)}
                        className={cn(
                          'w-full text-left rounded-md px-2 py-2 text-sm transition-colors',
                          isActive
                            ? 'bg-primary text-primary-foreground font-medium'
                            : 'text-foreground hover:bg-muted/60'
                        )}
                      >
                        {item.label}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>

        {/* Mobile: sub-items for active activity only */}
        <div className="lg:hidden px-3 pb-3">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">
            {BUILDER_NAV_GROUPS.find((g) => g.activityId === activeActivity)?.label}
          </p>
          <ul className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1">
            {BUILDER_NAV_GROUPS.filter((g) => g.activityId === activeActivity).flatMap((g) =>
              g.items.map((item) => (
                <li key={item.id} className="shrink-0">
                  <button
                    type="button"
                    onClick={() => onSelectPanel(item.id)}
                    className={cn(
                      'rounded-full px-3 py-1.5 text-xs border transition-colors',
                      activePanel === item.id
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-background border-border text-muted-foreground'
                    )}
                  >
                    {item.label}
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      </ScrollArea>
    </nav>
  );
}

/** @internal for tests */
export type { ProductActivityId };
